<!-- components/Landing/SeccionBalanceo.vue -->
<template>
  <section class="w-full">
    <!-- =========================================================
      SECCIÓN 1: ¿Qué es el balanceo?
      Legacy:
        - Desktop: padding 70px 130px
        - Mobile:  padding 50px 20px + column-reverse
    ========================================================== -->
    <div :class="clases_contenedor.seccion_1">
      <div class="grid items-center gap-10 md:grid-cols-2 md:gap-12">
        <!-- Texto (mobile primero) -->
        <div class="order-1 md:order-2">
          <div class="flex items-start gap-2">
            <img
              src="/img/balanceo/vector_negro.png"
              alt=""
              width="20"
              height="26"
              class="mt-1 h-[26px] w-[20px] select-none"
              loading="lazy"
              decoding="async"
            />
            <h2 class="text-xl font-bold text-gray-900 md:text-2xl">
              ¿Qué es el balanceo?
            </h2>
          </div>

          <p class="mt-6 text-[15px] leading-6 text-gray-600 md:text-base md:leading-7">
            El balanceo es el proceso mediante el cual se equilibra el peso de los neumáticos y de
            las llantas. Si bien a primera vista todos los neumáticos lucen iguales, existen
            pequeñas diferencias en el peso de cada lado de éste. Durante el balanceo se corrigen
            estas imperfecciones mediante pequeños contrapesos que se colocan en la llanta.
          </p>
        </div>

        <!-- Carrusel (mobile segundo) -->
        <div class="order-2 flex md:order-1 md:justify-end">
          <div
            class="relative w-full max-w-[640px] overflow-hidden rounded-md border border-gray-200 bg-white"
            role="region"
            aria-roledescription="carousel"
            :aria-label="`Carrusel: ${titulo_carrusel}`"
            tabindex="0"
            @mouseenter="pausar_autoplay"
            @mouseleave="reanudar_autoplay"
            @focusin="pausar_autoplay"
            @focusout="reanudar_autoplay"
            @keydown.left.prevent="ir_anterior"
            @keydown.right.prevent="ir_siguiente"
          >
            <!-- Slide -->
            <div class="relative h-[240px] w-full bg-gray-100 sm:h-[280px] md:h-[320px]">
              <transition name="fade" mode="out-in">
                <img
                  :key="slide_actual.src"
                  :src="slide_actual.src"
                  :alt="slide_actual.alt"
                  class="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </transition>

              <!-- Indicadores (overlay, estilo bootstrap-like) -->
              <div class="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2">
                <button
                  v-for="(slide, i) in slides"
                  :key="slide.src"
                  type="button"
                  class="h-2 w-2 rounded-full border border-white/80 transition focus:outline-none focus:ring-2 focus:ring-white/70"
                  :class="i === indice_slide ? 'bg-white' : 'bg-white/30 hover:bg-white/60'"
                  :aria-label="`Ir al slide ${i + 1}`"
                  :aria-current="i === indice_slide ? 'true' : 'false'"
                  @click="ir_a(i)"
                />
              </div>

              <!-- Controles -->
              <button
                type="button"
                class="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white backdrop-blur-sm transition hover:bg-black/45 focus:outline-none focus:ring-2 focus:ring-white/70"
                aria-label="Anterior"
                @click="ir_anterior"
              >
                <span class="block text-lg leading-none">‹</span>
              </button>

              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white backdrop-blur-sm transition hover:bg-black/45 focus:outline-none focus:ring-2 focus:ring-white/70"
                aria-label="Siguiente"
                @click="ir_siguiente"
              >
                <span class="block text-lg leading-none">›</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =========================================================
      SECCIÓN 2: Diferencia alineación vs balanceo
      Legacy:
        - background #F8F8F8
        - Desktop: padding 70px 190px (y en pantallas grandes se ajusta)
        - Mobile:  padding 50px 20px
    ========================================================== -->
    <div class="w-full bg-[#F8F8F8]">
      <div :class="clases_contenedor.seccion_2">
        <h2 class="text-center text-xl font-bold text-gray-900 md:text-2xl">
          ¿Cuál es la diferencia entre alineación y balanceo?
        </h2>

        <div class="mt-12 grid gap-12 md:grid-cols-2 md:gap-10">
          <!-- Alineación -->
          <div class="flex flex-col items-center text-center">
            <img
              src="/img/balanceo/balanceo.png"
              alt="Iconografía alineación"
              class="mb-7 w-full max-w-[260px] select-none transition-transform duration-1000 hover:scale-110"
              loading="lazy"
              decoding="async"
            />

            <div class="flex items-center gap-2">
              <img
                src="/img/balanceo/vector_negro.png"
                alt=""
                width="10"
                height="16"
                class="h-[16px] w-[10px] select-none"
                loading="lazy"
                decoding="async"
              />
              <h3 class="text-sm font-bold tracking-wide text-gray-900 md:text-base">
                LA ALINEACIÓN
              </h3>
            </div>

            <p class="mt-5 max-w-[28rem] text-[15px] leading-6 text-gray-600 md:text-base md:leading-7">
              es un proceso que permite ajustar los ángulos de las ruedas, manteniéndolas
              perpendiculares al suelo y paralelas entre sí.
            </p>
          </div>

          <!-- Balanceo -->
          <div class="flex flex-col items-center text-center">
            <img
              src="/img/balanceo/alineacion.png"
              alt="Iconografía balanceo"
              class="mb-7 w-full max-w-[260px] select-none transition-transform duration-1000 hover:scale-110"
              loading="lazy"
              decoding="async"
            />

            <div class="flex items-center gap-2">
              <img
                src="/img/balanceo/vector_negro.png"
                alt=""
                width="10"
                height="16"
                class="h-[16px] w-[10px] select-none"
                loading="lazy"
                decoding="async"
              />
              <h3 class="text-sm font-bold tracking-wide text-gray-900 md:text-base">
                EL BALANCEO
              </h3>
            </div>

            <p class="mt-5 max-w-[30rem] text-[15px] leading-6 text-gray-600 md:text-base md:leading-7">
              es un proceso que permite equilibrar el peso de todas las ruedas de un vehículo para
              que giren sin vibrar a altas velocidades.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- =========================================================
      SECCIÓN 3: ¿Cuándo balancear un neumático?
      Legacy:
        - Fondo con imagen + texto blanco
        - Imagen neu-volante:
            Desktop: absolute; right:0; top:-30px; width:55%
            Mobile:  position: initial; width:100%; margin-top:-30px
    ========================================================== -->
    <div class="relative w-full bg-[url('/img/balanceo/fondo_balanceo2.jpg')] bg-cover bg-center">
      <!-- Overlay para legibilidad -->
      <div class="absolute inset-0 bg-black/50" aria-hidden="true" />

      <div :class="clases_contenedor.seccion_3" class="relative">
        <div class="grid items-center gap-10 md:grid-cols-2 md:gap-12">
          <!-- Imagen (desktop a la izquierda, con absolute dentro del bloque) -->
          <div class="order-2 md:order-1">
            <div class="relative min-h-[220px] md:min-h-[320px]">
              <img
                src="/img/balanceo/neu-volante.png"
                alt="Neumático y volante"
                class="w-full -mt-8 select-none transition-transform duration-1000 hover:scale-110 md:absolute md:right-0 md:top-[-30px] md:mt-0 md:w-[60%] xl:w-[60%]"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>

          <!-- Texto -->
          <div class="order-1 md:order-2">
            <div class="flex items-start gap-2">
              <img
                src="/img/balanceo/Vector.png"
                alt=""
                width="20"
                height="26"
                class="mt-1 h-[26px] w-[20px] select-none"
                loading="lazy"
                decoding="async"
              />
              <h2 class="text-xl font-bold text-white md:text-2xl">
                ¿Cuándo balancear un neumático?
              </h2>
            </div>

            <p class="mt-6 text-[15px] leading-6 text-white/90 md:text-base md:leading-7">
              El balanceo se debe realizar en el momento que se perciba que el volante vibra o
              genera ruidos, ya sea en las ruedas, en el piso del auto o asientos. Es importante que
              este servicio se realice para permitir un mejor mantenimiento y brindar estabilidad y
              seguridad al conducir.
            </p>

            <p class="mt-4 text-[15px] leading-6 text-white/90 md:text-base md:leading-7">
              Se recomienda realizar el balanceo de neumáticos cada 10.000 kilómetros aprox.
              Asimismo, cuando se instalan neumáticos nuevos, es importante que sean balanceados
              antes de utilizarlos para que te asegures de que todo se encuentra en perfectas
              condiciones.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- =========================================================
      SECCIÓN 4: Items finales + autos laterales
      Legacy:
        - Desktop: auto1 izquierda / auto2 derecha (columnas)
        - Mobile: bloque central con padding 20px y auto2 como fondo
    ========================================================== -->
    <div class="relative w-full bg-white">
      <!-- Laterales (solo md+) -->
      <img
        src="/img/balanceo/auto1.png"
        alt=""
        class="pointer-events-none absolute left-0 top-0 hidden h-full select-none md:block"
        loading="lazy"
        decoding="async"
      />
      <img
        src="/img/balanceo/auto2.png"
        alt=""
        class="pointer-events-none absolute right-0 top-0 hidden h-full select-none md:block"
        loading="lazy"
        decoding="async"
      />

      <div :class="clases_contenedor.seccion_4">
        <div
          class="relative z-10 md:bg-transparent p-5 md:p-0"
          :style="{ backgroundImage: 'url(/img/balanceo/auto2.png)' }"
          style="background-size: contain; background-position: right bottom; background-repeat: no-repeat;"
        >
          <h2 class="text-xl font-bold text-gray-900 md:text-2xl">
            Por último, los neumáticos se tienen que balancear en caso de problemas específicos,
            tales como:
          </h2>

          <div class="mt-8 space-y-6">
            <div class="flex items-center gap-4">
              <img
                src="/img/balanceo/ico1.png"
                alt=""
                class="h-12 w-12 select-none"
                loading="lazy"
                decoding="async"
              />
              <p class="text-[15px] leading-6 text-gray-700 md:text-base md:leading-7">
                Al conducir a alta velocidad, el volante comience a vibrar de forma considerable.
              </p>
            </div>

            <div class="flex items-center gap-4">
              <img
                src="/img/balanceo/ico2.png"
                alt=""
                class="h-12 w-12 select-none"
                loading="lazy"
                decoding="async"
              />
              <p class="text-[15px] leading-6 text-gray-700 md:text-base md:leading-7">
                Al manejar a altas velocidades, el asiento o el piso vibren notoriamente.
              </p>
            </div>

            <div class="flex items-start gap-4">
              <img
                src="/img/balanceo/ico3.png"
                alt=""
                class="mt-1 h-12 w-12 select-none"
                loading="lazy"
                decoding="async"
              />
              <p class="text-[15px] leading-6 text-gray-700 md:text-base md:leading-7">
                Se recomienda, también, hacer un balanceo después de todas aquellas reparaciones a
                la suspensión, frenos, discos o cualquier otra herramienta que implique el
                desmontaje del neumático.
              </p>
            </div>
          </div>

          <!-- Respiro en mobile para que el fondo no “choque” visualmente -->
          <div class="h-6 md:hidden" aria-hidden="true" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
// =============================================================
// Carrusel simple, robusto y SSR-friendly (Vue3/Nuxt3):
// - Sin Bootstrap / sin plugins.
// - Indicadores overlay tipo bootstrap-like.
// - Teclado: flechas izquierda/derecha.
// - Autoplay opcional, pausado en hover/focus.
// - Respeta prefers-reduced-motion.
// =============================================================
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  autoplay: { type: Boolean, default: true },
  autoplay_intervalo_ms: { type: Number, default: 5000 },
  forzar_autoplay: { type: Boolean, default: true }
})

const titulo_carrusel = 'Balanceo'

const slides = [
  { src: '/img/balanceo/carr1.png', alt: 'Balanceo - imagen 1' },
  { src: '/img/balanceo/carr2.png', alt: 'Balanceo - imagen 2' },
  { src: '/img/balanceo/carr3.png', alt: 'Balanceo - imagen 3' }
]

const indice_slide = ref(0)
const slide_actual = computed(() => slides[indice_slide.value])

/**
 * Normaliza índice a rango válido [0..n-1], evitando errores por negativos.
 */
const normalizar_indice = (nuevo_indice) => {
  const total = slides.length
  if (total <= 0) return 0
  return ((nuevo_indice % total) + total) % total
}

const ir_a = (nuevo_indice) => {
  indice_slide.value = normalizar_indice(nuevo_indice)
}

const ir_anterior = () => {
  ir_a(indice_slide.value - 1)
}

const ir_siguiente = () => {
  ir_a(indice_slide.value + 1)
}

// ---- Autoplay (opcional) ----
let timer_autoplay = null
let reduce_motion = false

const limpiar_autoplay = () => {
  if (timer_autoplay) {
    clearInterval(timer_autoplay)
    timer_autoplay = null
  }
}

const iniciar_autoplay = () => {
  limpiar_autoplay()

  if (!props.autoplay) return
  if (reduce_motion && !props.forzar_autoplay) return
  if (slides.length <= 1) return

  const intervalo = Math.max(1500, Number(props.autoplay_intervalo_ms) || 5000)
  timer_autoplay = setInterval(() => {
    ir_siguiente()
  }, intervalo)
}

const pausar_autoplay = () => {
  limpiar_autoplay()
}

const reanudar_autoplay = () => {
  iniciar_autoplay()
}

onMounted(() => {
  try {
    reduce_motion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
  } catch (error) {
    reduce_motion = false
  }
  iniciar_autoplay()
})

onBeforeUnmount(() => {
  limpiar_autoplay()
})

/**
 * Layout: paddings equivalentes al legacy (aprox exactos).
 * - Mobile: py 50px, px 20px
 * - Desktop: según cada sección (130 / 190)
 */
const clases_contenedor = {
  seccion_1: 'mx-auto w-full max-w-[1600px] px-[20px] py-[50px] md:px-[130px] md:py-[70px]',
  seccion_2: 'mx-auto w-full max-w-[1600px] px-[20px] py-[50px] md:px-[190px] md:py-[70px] 2xl:px-[130px]',
  seccion_3: 'mx-auto w-full max-w-[1600px] px-[20px] py-[50px] md:px-[130px] md:py-[70px] xl:px-[50px]',
  seccion_4: 'mx-auto w-full max-w-[1600px] px-[20px] py-[30px] md:px-[130px] md:py-[30px]'
}
</script>

<style scoped>
/* Fade liviano para el carrusel (sin libs) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 240ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
